/**
 * Integrytas Group - Base App (MOBILE)
 * Page: DQE.RULES — mobile-native.
 *
 * Touch-first counterpart of src/pages/admin/DataQualityRulesPage.tsx. A DISTINCT
 * presentation (compact KPI row + dense card list + facet chips + bottom-sheet
 * detail + edit sheet) over the SAME data/logic layer only:
 *   - reads via apiDb (same dqe_rule table/columns),
 *   - every write (activate/deactivate + scalar edit) routes through the Common
 *     Routine via routedUpdate — §2 single write path; never a direct apiDb write.
 * It never re-renders the desktop grid; §16.
 *
 * dqe_rule has no FK / _tl columns: internal_code + entity_name are natural
 * identifiers, and rule_type / severity / applies_on are CHECK enums whose labels
 * are localised through t() labelled constants (§17.B11) — never the bare literal.
 * The JSON rule_def editor stays on the desktop; on mobile the touch-friendly
 * scalar flags are edited here (mirrors MobileOrgEditSheet keeping the _tl name
 * read-only), so business logic and data live once.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Inbox, Power, X, SquarePen, ShieldCheck, ShieldAlert, RefreshCw, ListChecks } from 'lucide-react';
import { apiDb } from '../../lib/api_client';
import { routedUpdate } from '../../lib/routed_writes';
import {
  MobilePageHeader, MobileStatStrip, MobileContentCard, MobileCardRow, MobileDetailRow,
  MobileFilterChips, MobileStatusPill, type MobilePillTone,
} from '../components';
import type { AuthSession } from '../../types/auth';
import type { MobilePageComponentProps } from '../mobile_page_components';
import { useFormatDateTime } from '../../lib/format_date';
import { hasAccess } from '../../types/auth';

const PAGE_CODE = 'DQE.RULES';
import {
  loadBreachSnapshots,
  runBreachScan,
  summariseBreaches,
  loadBreachRows,
  pickBreachColumns,
  ruleReferencedColumns,
  BREACH_ROWS_DEFAULT_LIMIT,
  type BreachSnapshot,
  type BreachRows,
} from '../../lib/dqe_breach_scan';

type RuleType = 'REQUIRED' | 'FORMAT' | 'CROSS_FIELD';
type Severity = 'ERROR' | 'WARNING';
type AppliesOn = 'ADD' | 'CHANGE' | 'BOTH';

interface MobileRuleRow {
  data_quality_rule_id: number;
  internal_code: string;
  entity_name: string;
  rule_type: RuleType;
  severity: Severity;
  applies_on: AppliesOn;
  error_message: string | null;
  is_active: 'Y' | 'N';
  description: string | null;
}

interface EnumOption<T extends string> {
  value: T;
  labelKey: string;
  label: string;
}

// §17.B11: CHECK-enum labels come from labelled constants, never bare literals.
const RULE_TYPE_OPTIONS: readonly EnumOption<RuleType>[] = [
  { value: 'REQUIRED', labelKey: 'mobile.dqe.type.REQUIRED', label: 'Required' },
  { value: 'FORMAT', labelKey: 'mobile.dqe.type.FORMAT', label: 'Format' },
  { value: 'CROSS_FIELD', labelKey: 'mobile.dqe.type.CROSS_FIELD', label: 'Cross-field' },
];

const SEVERITY_OPTIONS: readonly EnumOption<Severity>[] = [
  { value: 'ERROR', labelKey: 'mobile.dqe.severity.ERROR', label: 'Error (blocks)' },
  { value: 'WARNING', labelKey: 'mobile.dqe.severity.WARNING', label: 'Warning (passes)' },
];

const APPLIES_ON_OPTIONS: readonly EnumOption<AppliesOn>[] = [
  { value: 'BOTH', labelKey: 'mobile.dqe.on.BOTH', label: 'Add & Change' },
  { value: 'ADD', labelKey: 'mobile.dqe.on.ADD', label: 'Add' },
  { value: 'CHANGE', labelKey: 'mobile.dqe.on.CHANGE', label: 'Change' },
];

type StatusFacet = 'all' | 'active' | 'inactive';

type Translate = ReturnType<typeof useTranslation>['t'];

function enumLabel<T extends string>(t: Translate, options: readonly EnumOption<T>[], value: T): string {
  const opt = options.find((o) => o.value === value);
  return opt ? t(opt.labelKey, opt.label) : value;
}

// Severity maps onto the allowed status-pill palette: ERROR = danger, WARNING = amber/wait.
function severityTone(severity: Severity): MobilePillTone {
  return severity === 'ERROR' ? 'no' : 'wait';
}

export function MobileDataQualityRulesPage({ session }: MobilePageComponentProps) {
  // §6 RBAC: csm_role_page_access decides which write affordances this user
  // gets. The server refuses the write regardless (RLS via the Common
  // Routine); this stops the page offering an action that can only error.
  const canChange = hasAccess(session, PAGE_CODE, 'change');

  const { t } = useTranslation();
  const fmtDateTime = useFormatDateTime();
  const [rows, setRows] = useState<MobileRuleRow[]>([]);
  const [filter, setFilter] = useState('');
  const [statusFacet, setStatusFacet] = useState<StatusFacet>('active');
  const [blockingOnly, setBlockingOnly] = useState(false);
  const [selected, setSelected] = useState<MobileRuleRow | null>(null);
  const [editing, setEditing] = useState<MobileRuleRow | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Breach reporting (shared logic layer): per-rule snapshots, the scan action,
  // and the drill-through target. Snapshots join to rows by rule id.
  const [snapshots, setSnapshots] = useState<Map<number, BreachSnapshot>>(new Map());
  const [scanning, setScanning] = useState(false);
  const [reviewRule, setReviewRule] = useState<MobileRuleRow | null>(null);

  const reload = async () => {
    setBusy(true);
    setError(null);
    try {
      const { data, error: e } = await apiDb
        .from('dqe_rule')
        .select('data_quality_rule_id, internal_code, entity_name, rule_type, severity, applies_on, error_message, is_active, description')
        .order('entity_name')
        .order('internal_code');
      if (e) throw e;
      setRows((data ?? []) as MobileRuleRow[]);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const reloadSnapshots = async () => {
    const { byRule, error: e } = await loadBreachSnapshots();
    if (!e) setSnapshots(byRule);
  };

  const runScan = async () => {
    setScanning(true);
    setError(null);
    const { byRule, error: e } = await runBreachScan(session);
    setScanning(false);
    if (e) { setError(e.message); return; }
    setSnapshots(byRule);
  };

  useEffect(() => { void reload(); void reloadSnapshots(); }, []);

  const breachSummary = useMemo(() => summariseBreaches(snapshots.values()), [snapshots]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter((r) => {
      if (statusFacet === 'active' && r.is_active !== 'Y') return false;
      if (statusFacet === 'inactive' && r.is_active !== 'N') return false;
      if (blockingOnly && r.severity !== 'ERROR') return false;
      if (!q) return true;
      return (
        r.internal_code.toLowerCase().includes(q) ||
        r.entity_name.toLowerCase().includes(q) ||
        r.rule_type.toLowerCase().includes(q)
      );
    });
  }, [rows, filter, statusFacet, blockingOnly]);

  const kpis = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.is_active === 'Y').length;
    const blocking = rows.filter((r) => r.is_active === 'Y' && r.severity === 'ERROR').length;
    return { total, active, blocking };
  }, [rows]);

  const statusFacets: { key: StatusFacet; label: string }[] = [
    { key: 'all', label: t('mobile.dqe.all', 'All') },
    { key: 'active', label: t('common.boolean.active', 'Active') },
    { key: 'inactive', label: t('common.boolean.inactive', 'Inactive') },
  ];

  const toggleActive = async (r: MobileRuleRow) => {
    setActionBusy(true);
    setError(null);
    const next = r.is_active === 'Y' ? 'N' : 'Y';
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'dqe_rule', 'data_quality_rule_id', r.data_quality_rule_id, { is_active: next });
    setActionBusy(false);
    if (e) { setError(e.message); return; }
    setSelected(null);
    void reload();
  };

  return (
    <section className="m-screen">
      <MobilePageHeader
        title={t('mobile.dqe.title', 'Data Quality Rules')}
        description={t('mobile.dqe.description', 'Validation rules enforced before each write, and their breaches.')}
      />

      <MobileStatStrip
        stats={[
          { value: kpis.total, label: t('mobile.dqe.kpi.total', 'Rules') },
          { value: kpis.active, label: t('common.boolean.active', 'Active') },
          { value: kpis.blocking, label: t('mobile.dqe.kpi.blocking', 'Blocking') },
        ]}
      />

      <div className="m-card">
        <div className="m-field" style={{ alignItems: 'center' }}>
          <span className="m-k">
            {breachSummary.lastScannedAt
              ? t('mobile.dqe.breach.summary', '{{rules}} breaching · {{total}} breaches', { rules: breachSummary.rulesBreaching, total: breachSummary.totalBreaches })
              : t('mobile.dqe.breach.never', 'Not scanned yet')}
          </span>
          <button
            type="button"
            className="m-btn m-btn-ghost"
            disabled={scanning}
            onClick={() => void runScan()}
            style={{ width: 'auto', padding: '0 12px' }}
          >
            <RefreshCw size={16} />
            {scanning ? t('mobile.dqe.breach.scanning', 'Scanning…') : t('mobile.dqe.breach.scan', 'Scan')}
          </button>
        </div>
        {breachSummary.lastScannedAt && (
          <div className="m-formrow-s" style={{ paddingTop: 4 }}>
            {t('mobile.dqe.breach.as_of', 'as of {{when}}', { when: fmtDateTime(breachSummary.lastScannedAt) })}
          </div>
        )}
      </div>

      <div className="m-searchbox">
        <Search />
        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder={t('mobile.dqe.search', 'Search rules')}
          aria-label={t('mobile.dqe.search', 'Search rules')}
        />
      </div>

      <MobileFilterChips
        chips={statusFacets.map((f) => ({ value: f.key, label: f.label }))}
        isActive={(v) => statusFacet === v}
        onSelect={(v) => setStatusFacet(v as StatusFacet)}
      />

      <MobileFilterChips
        chips={[{ value: 'blocking', label: t('mobile.dqe.blocking_only', 'Blocking') }]}
        isActive={() => blockingOnly}
        onSelect={() => setBlockingOnly((v) => !v)}
      />

      {error && (
        <MobileContentCard>
          <div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div>
        </MobileContentCard>
      )}

      {filtered.length === 0 && !busy ? (
        <MobileContentCard><div className="m-empty"><Inbox /> {t('mobile.dqe.none', 'No rules match')}</div></MobileContentCard>
      ) : (
        <MobileContentCard list>
          {filtered.map((r) => {
            const s = snapshots.get(r.data_quality_rule_id);
            const breaching = s && s.is_evaluable === 'Y' && s.breach_count > 0;
            return (
              <MobileCardRow
                key={r.data_quality_rule_id}
                lead={r.severity === 'ERROR' ? <ShieldAlert /> : <ShieldCheck />}
                leadTone={r.severity === 'ERROR' ? 'red' : 'amber'}
                title={r.internal_code}
                subtitle={`${r.entity_name} · ${enumLabel(t, RULE_TYPE_OPTIONS, r.rule_type)}`}
                trailing={
                  <>
                    {breaching && (
                      <MobileStatusPill tone="no">{t('mobile.dqe.breach.count_pill', '{{count}} breaching', { count: s.breach_count })}</MobileStatusPill>
                    )}
                    <MobileStatusPill tone={r.is_active === 'Y' ? 'ok' : 'no'}>
                      {r.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                    </MobileStatusPill>
                  </>
                }
                chevron
                onClick={() => setSelected(r)}
              />
            );
          })}
        </MobileContentCard>
      )}

      {/* Detail bottom-sheet */}
      <div className={'m-scrim' + (selected ? ' m-open' : '')} onClick={() => { if (!actionBusy) setSelected(null); }} />
      <div className={'m-sheet' + (selected ? ' m-open' : '')} aria-label={t('mobile.dqe.detail', 'Rule detail')}>
        <div className="m-grabber" />
        {selected && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ShieldCheck size={20} /></span>
              <span>
                <div className="m-nm">{selected.internal_code}</div>
                <div className="m-em">{selected.entity_name}</div>
              </span>
            </div>
            <MobileContentCard>
              <MobileDetailRow
                label={t('mobile.dqe.status', 'Status')}
                value={
                  <MobileStatusPill tone={selected.is_active === 'Y' ? 'ok' : 'no'}>
                    {selected.is_active === 'Y' ? t('common.boolean.active', 'Active') : t('common.boolean.inactive', 'Inactive')}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.dqe.type_lbl', 'Type')} value={enumLabel(t, RULE_TYPE_OPTIONS, selected.rule_type)} />
              <MobileDetailRow
                label={t('mobile.dqe.severity_lbl', 'Severity')}
                value={
                  <MobileStatusPill tone={severityTone(selected.severity)}>
                    {enumLabel(t, SEVERITY_OPTIONS, selected.severity)}
                  </MobileStatusPill>
                }
              />
              <MobileDetailRow label={t('mobile.dqe.on_lbl', 'Applies on')} value={enumLabel(t, APPLIES_ON_OPTIONS, selected.applies_on)} />
              {(() => {
                const s = snapshots.get(selected.data_quality_rule_id);
                if (!s) return null;
                return (
                  <MobileDetailRow
                    label={t('mobile.dqe.breach.field', 'Breaches')}
                    value={s.is_evaluable !== 'Y'
                      ? t('mobile.dqe.breach.na', 'n/a')
                      : t('mobile.dqe.breach.field_value', '{{count}} · scanned {{when}}', { count: s.breach_count, when: fmtDateTime(s.last_scanned_at) })}
                  />
                );
              })()}
              <MobileDetailRow label={t('mobile.dqe.error_message', 'Error message')} value={selected.error_message || t('common.none', 'None')} />
              <MobileDetailRow label={t('mobile.dqe.description', 'Description')} value={selected.description || t('common.none', 'None')} />
            </MobileContentCard>
            <div className="m-actionbar">
              {(() => {
                const s = snapshots.get(selected.data_quality_rule_id);
                return s && s.is_evaluable === 'Y' && s.breach_count > 0
                  ? <button
                      type="button"
                      className="m-btn m-btn-ghost"
                      disabled={actionBusy}
                      onClick={() => { setReviewRule(selected); setSelected(null); }}
                    >
                      <ListChecks size={16} />
                      {t('mobile.dqe.breach.review', 'Review breaches')}
                    </button>
                  : null;
              })()}
              {canChange && (<button
                type="button"
                className="m-btn m-btn-primary"
                disabled={actionBusy}
                onClick={() => { setEditing(selected); setSelected(null); }}
              >
                <SquarePen size={16} />
                {t('mobile.dqe.edit', 'Edit')}
              </button>)}
              {canChange && (<button
                type="button"
                className={'m-btn ' + (selected.is_active === 'Y' ? 'm-btn-danger' : 'm-btn-ghost')}
                disabled={actionBusy}
                onClick={() => void toggleActive(selected)}
              >
                <Power size={16} />
                {selected.is_active === 'Y' ? t('users.list.actions.deactivate', 'Deactivate') : t('users.list.actions.activate', 'Activate')}
              </button>)}
              <button type="button" className="m-btn m-btn-ghost" disabled={actionBusy} onClick={() => setSelected(null)} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>

      <MobileRuleEditSheet
        session={session}
        row={editing}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); void reload(); }}
      />

      <MobileDqeBreachSheet
        rule={reviewRule}
        onClose={() => setReviewRule(null)}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// Breach drill-through — a touch bottom-sheet listing the ACTUAL rows that
// currently violate one rule (read-only, over the shared loadBreachRows layer;
// RLS-respecting). Its own mobile presentation; §16. Columns are resolved with
// the shared helpers so no raw surrogate id leads the view (§17.B4).
// ---------------------------------------------------------------------------

const ISO_DATETIME = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}/;
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;

function MobileDqeBreachSheet({ rule, onClose }: { rule: MobileRuleRow | null; onClose: () => void }) {
  const { t } = useTranslation();
  const fmtDate = useFormatDateTime();
  const [data, setData] = useState<BreachRows | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!rule) { setData(null); setError(null); return; }
    let cancelled = false;
    setBusy(true); setError(null);
    void loadBreachRows(rule.data_quality_rule_id, BREACH_ROWS_DEFAULT_LIMIT).then(({ data: d, error: e }) => {
      if (cancelled) return;
      if (e) { setError(e.message); setData(null); } else setData(d);
      setBusy(false);
    });
    return () => { cancelled = true; };
  }, [rule]);

  const rows = useMemo(() => data?.rows ?? [], [data]);
  const columns = useMemo(
    () => (rule ? pickBreachColumns(rows, ruleReferencedColumns(rule.rule_type, undefined), 4) : []),
    [rows, rule],
  );

  const formatCell = (value: unknown): string => {
    if (value === null || value === undefined || value === '') return '—';
    if (typeof value === 'string') {
      if (ISO_DATETIME.test(value) || ISO_DATE.test(value)) return fmtDate(value);
      return value;
    }
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const open = rule !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.dqe.breach.sheet', 'Breaching rows')}>
        <div className="m-grabber" />
        {rule && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><ListChecks size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.dqe.breach.sheet_title', 'Breaching rows')}</div>
                <div className="m-em">{rule.internal_code} · {data?.entity_name ?? rule.entity_name}</div>
              </span>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}
            {busy && <div className="m-card"><div className="m-empty">{t('mobile.dqe.breach.loading', 'Loading…')}</div></div>}

            {!busy && !error && data?.is_evaluable === 'N' && (
              <div className="m-card"><div className="m-empty">{t('mobile.dqe.breach.not_evaluable', 'Rule cannot be evaluated against the current data.')}</div></div>
            )}
            {!busy && !error && data?.is_evaluable === 'Y' && rows.length === 0 && (
              <div className="m-card"><div className="m-empty"><Inbox /> {t('mobile.dqe.breach.none', 'No rows currently breach this rule.')}</div></div>
            )}
            {!busy && !error && data?.is_evaluable === 'Y' && rows.length > 0 && (
              <div className="m-card"><div className="m-list">
                {rows.map((row, i) => (
                  <div key={i} className="m-row" style={{ display: 'block' }}>
                    {columns.map((col) => (
                      <div key={col} className="m-field">
                        <span className="m-k" style={{ fontFamily: 'monospace' }}>{col}</span>
                        <span className="m-v">{formatCell(row[col])}</span>
                      </div>
                    ))}
                  </div>
                ))}
                {rows.length >= BREACH_ROWS_DEFAULT_LIMIT && (
                  <div className="m-formrow-s" style={{ padding: 8 }}>
                    {t('mobile.dqe.breach.capped', 'First {{limit}} shown.', { limit: BREACH_ROWS_DEFAULT_LIMIT })}
                  </div>
                )}
              </div></div>
            )}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('mobile.close', 'Close')}>
                <X size={16} />
                {t('common.close', 'Close')}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Edit sheet — the touch-friendly scalar flags (severity, applies-on, active,
// error message, description), saved through the Common Routine (routedUpdate)
// — §2. The internal code, entity and rule_type are structural identity + the
// JSON rule_def is edited on the desktop, so they are shown read-only here
// (mirrors MobileOrgEditSheet keeping the _tl name read-only).
// ---------------------------------------------------------------------------

interface MobileRuleEditSheetProps {
  session: AuthSession;
  row: MobileRuleRow | null;
  onClose: () => void;
  onSaved: () => void;
}

function MobileRuleEditSheet({ session, row, onClose, onSaved }: MobileRuleEditSheetProps) {
  const { t } = useTranslation();
  const [severity, setSeverity] = useState<Severity>('ERROR');
  const [appliesOn, setAppliesOn] = useState<AppliesOn>('BOTH');
  const [active, setActive] = useState(true);
  const [errMsg, setErrMsg] = useState('');
  const [description, setDescription] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form whenever a different rule is opened.
  useEffect(() => {
    if (!row) return;
    setSeverity(row.severity);
    setAppliesOn(row.applies_on);
    setActive(row.is_active === 'Y');
    setErrMsg(row.error_message ?? '');
    setDescription(row.description ?? '');
    setError(null);
  }, [row]);

  const save = async () => {
    if (!row) return;
    setBusy(true);
    setError(null);
    // §2: route through the Common Routine (forensic stamp + audit + hooks).
    const { error: e } = await routedUpdate(session, 'dqe_rule', 'data_quality_rule_id', row.data_quality_rule_id, {
      severity,
      applies_on: appliesOn,
      is_active: active ? 'Y' : 'N',
      error_message: errMsg.trim() || null,
      description: description.trim() || null,
    });
    setBusy(false);
    if (e) { setError(e.message); return; }
    onSaved();
  };

  const open = row !== null;

  return (
    <>
      <div className={'m-scrim' + (open ? ' m-open' : '')} onClick={() => { if (!busy) onClose(); }} />
      <div className={'m-sheet' + (open ? ' m-open' : '')} aria-label={t('mobile.dqe.edit_sheet', 'Edit rule')}>
        <div className="m-grabber" />
        {row && (
          <>
            <div className="m-sheet-id">
              <span className="m-av"><SquarePen size={20} /></span>
              <span>
                <div className="m-nm">{t('mobile.dqe.edit_title', 'Edit rule')}</div>
                <div className="m-em">{row.internal_code}</div>
              </span>
            </div>

            <div className="m-fieldcard">
              <div className="m-formrow">
                <label>{t('mobile.dqe.entity', 'Entity (table)')}</label>
                <div className="m-formrow-ro">
                  {row.entity_name}
                  <span className="m-formrow-hint"> · {enumLabel(t, RULE_TYPE_OPTIONS, row.rule_type)}</span>
                </div>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dqe-severity">{t('mobile.dqe.severity_lbl', 'Severity')}</label>
                <select id="m-dqe-severity" className="m-sel" value={severity} onChange={(e) => setSeverity(e.target.value as Severity)}>
                  {SEVERITY_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.label)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dqe-on">{t('mobile.dqe.on_lbl', 'Applies on')}</label>
                <select id="m-dqe-on" className="m-sel" value={appliesOn} onChange={(e) => setAppliesOn(e.target.value as AppliesOn)}>
                  {APPLIES_ON_OPTIONS.map((o) => <option key={o.value} value={o.value}>{t(o.labelKey, o.label)}</option>)}
                </select>
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dqe-errmsg">{t('mobile.dqe.error_message', 'Custom error message')}</label>
                <input
                  id="m-dqe-errmsg"
                  className="m-inp"
                  value={errMsg}
                  onChange={(e) => setErrMsg(e.target.value)}
                  placeholder={t('mobile.dqe.error_message_placeholder', 'Optional')}
                />
              </div>
              <div className="m-formrow">
                <label htmlFor="m-dqe-desc">{t('mobile.dqe.description', 'Description')}</label>
                <input
                  id="m-dqe-desc"
                  className="m-inp"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder={t('mobile.dqe.description_placeholder', 'Optional')}
                />
              </div>
              <div className="m-formrow m-formrow-inline">
                <span>
                  <div className="m-formrow-l">{t('common.boolean.active', 'Active')}</div>
                  <div className="m-formrow-s">{t('mobile.dqe.active_hint', 'Enforced before each mutation')}</div>
                </span>
                <button
                  type="button"
                  role="switch"
                  aria-checked={active}
                  className={'m-switch ' + (active ? 'm-on' : 'm-off')}
                  onClick={() => setActive((v) => !v)}
                >
                  <span className="m-knob" />
                </button>
              </div>
              <div className="m-formrow">
                <label>{t('mobile.dqe.rule_definition', 'Rule definition')}</label>
                <div className="m-formrow-ro">
                  {t('mobile.dqe.rule_definition_desktop', 'JSON rule definition is edited on the desktop')}
                </div>
              </div>
            </div>

            {error && <div className="m-card"><div className="m-empty" style={{ color: 'var(--color-danger)' }}>{error}</div></div>}

            <div className="m-actionbar">
              <button type="button" className="m-btn m-btn-primary" disabled={busy} onClick={() => void save()}>
                {busy ? t('common.saving', 'Saving…') : t('common.save', 'Save changes')}
              </button>
              <button type="button" className="m-btn m-btn-ghost" disabled={busy} onClick={onClose} aria-label={t('common.cancel', 'Cancel')}>
                <X size={16} />
              </button>
            </div>
          </>
        )}
      </div>
    </>
  );
}
